import { ErrorMessage } from '@hookform/error-message'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Checkbox, Col, Flex, Form, Input, notification, Select, Skeleton, Typography } from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import dayjs from 'dayjs'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import EmailSwitchers from 'src/app/features/feature-business-administration/user-roles/add-user-role/EmailSwitchers'
import Expirations from 'src/app/features/feature-business-administration/user-roles/add-user-role/Expirations'
import NavigationOptions from 'src/app/features/feature-business-administration/user-roles/add-user-role/NavigationOptions'
import Switchers from 'src/app/features/feature-business-administration/user-roles/add-user-role/Switchers'
import { DEFAULT_VIEW_ITEMS } from 'src/app/features/feature-business-administration/user-roles/edit-user-role/constants'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { ActivationWarning } from 'src/app/shared/components/common/tag/ActivationWarning'
import { priGrpTypCodeName, USER_ROLE_STATUS } from 'src/app/shared/constants/common.constants'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { usePhone } from 'src/app/shared/hooks/usePhone'
import { EXPIRATION_TYPE } from 'src/app/shared/models/userRole.model'
import { getAccountList, getClientById, getClientListName } from 'src/app/shared/services/ClientSetupService'
import {
  getClientDetailById,
  putUpdateClientUser,
} from 'src/app/shared/services/ClientUserSetupService'
import { fetchPermission, fetchSubModuleItem, fetchUserNotificationType } from 'src/app/shared/services/UserRoleService'
import { getActiveAll } from 'src/app/shared/utils'
import { getEmailsSetup, getModulesSetup, getPermissionsSetup, setUpEmails, setUpModules, setUpPermissions } from 'src/app/store/userRoleSetup'
import { ArrowDownIcon } from 'src/assets/icons'
import ConfirmModal from './ConfirmModal'
import { items } from './example'
import './index.scss'
import SelectClient from './SelectClient'
import { EditFormValues } from './type'
import { generateValidationSchema } from './validation'

const EditClientUserSetup = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const { id } = params

  const [loading, setLoading] = useState<boolean>(false)
  const [clientList, setClientList] = useState<any[]>([])
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
  const [accountLoading, setAccountLoading] = useState<boolean>(false)
  const [accountList, setAccountList] = useState<any[]>([])
  const [isFetched, setIsFetched] = useState(false)
  const [isLocked, setIsLocked] = useState(false)
  const [prevStatus, setPrevStatus] = useState(1)
  const [isChange, setIsChange] = useState(false)
  const [duplicateUser, setDuplicateUser] = useState<any>(null)
  const modules = useSelector(getModulesSetup)
  const [selectedClientObj, setSelectedClientObj] = useState<any>(null)
  const dispatch = useDispatch()
  const [dependLoading, setDependLoading] = useState<boolean>(false)
  const [defaultPermission, setDefaultPermission] = useState<string>('')
  const defaultValues: any = useMemo(() => {
    return {
      username: '',
      firstName: '',
      lastName: '',
      emailAddress: '',
      noEmailContact: false,
      primaryPhoneNumber: '',
      mobileNumber: '',
      clientName: null,
      accountNames: [],
      assignAll: false,
      userLocked: false,
      archiveUser: false,
      hasCustomRole: true,
      navigationOptions: null,
      emails: null,
      permissions: null,
      expirations: EXPIRATION_TYPE.DOES_NOT_EXPIRE,
      expirationMinutes: null,
      expirationDays: null,
      primaryPhoneNumberRegion: '+1',
      mobileNumberRegion: '+1',
    }
  }, [])

  const formatData = (data: any) => {
    let checkboxStatus = {
      userLocked: false,
      archiveUser: false,
    }

    switch (data?.status) {
      case USER_ROLE_STATUS.LOCKED_AND_INACTIVE: {
        setIsLocked(true)
        checkboxStatus = {
          userLocked: true,
          archiveUser: true,
        }
        break
      }
      case USER_ROLE_STATUS.LOCKED: {
        setIsLocked(true)
        checkboxStatus = {
          userLocked: true,
          archiveUser: false,
        }
        break
      }
      case USER_ROLE_STATUS.INACTIVE: {
        checkboxStatus = {
          userLocked: false,
          archiveUser: true,
        }
        break
      }
      default:
        checkboxStatus = {
          userLocked: false,
          archiveUser: false,
        }
    }

    setPrevStatus(data?.status)

    const result = {
      username: data?.username,
      firstName: data?.firstName,
      lastName: data?.lastName,
      emailAddress: data?.email,
      noEmailContact: data?.isNotReceiveOtherMail,
      primaryPhoneNumber: data?.primaryPhoneNumber,
      mobileNumber: data?.mobileNumber,
      clientName: data?.clientId,
      accountNames: data?.accounts ?? [],
      assignAll: data?.isAssignAllNewAccounts,
      hasCustomRole: true,
      primaryPhoneNumberRegion: data?.primaryPrefix ?? '+1',
      mobileNumberRegion: data?.mobilePrefix ?? '+1',
      selectAllNavigations: data?.customRole?.navigationOptionIds?.length === getActiveAll(modules).length,
      navigationOptions: data?.customRole?.navigationOptionIds ?? [],
      selectAllEmails: data?.customRole?.userNotificationTypeIds?.length === emails?.length,
      emails: data?.customRole?.userNotificationTypeIds?.map((item: any) => ({ emailId: item })) ?? [],
      selectAllPermissions: data?.customRole?.permissionIds?.length === permissions?.length,
      permissions: data?.customRole?.permissionIds ?? [],
      expirations: data?.customRole?.expirationType ?? EXPIRATION_TYPE.DOES_NOT_EXPIRE,
      expirationMinutes: data?.customRole?.expirationMinutes ?? null,
      expirationDays: data?.customRole?.expirationDays ?? null,
      ...checkboxStatus,
    }

    return result
  }

  const getClientDetail = async () => {
    if (id) {
      try {
        await getClientListName(true).then((res: any) => {
          if (res?.data) {
            setClientList(
              res?.data?.map((item: any) => ({
                label: item.clientName,
                value: item.id,
                note: item.clientNotes,
              })),
            )
          }
        })
        const res = await getClientDetailById(id, true)
        setIsFetched(true)
        if (res?.data) {
          return formatData(res?.data)
        }
      } catch (error) {
        console.log('error', error)
      }
    }
    return defaultValues
  }

  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    setValue,
    resetField,
    setError,
    getValues,
    trigger,
    clearErrors,
  } = useForm<EditFormValues>({
    mode: 'all',
    defaultValues: async () => await getClientDetail(),
    resolver: yupResolver(generateValidationSchema(t) as any),
  })

  const checkStatus = () => {
    if (getStatus(getValues('userLocked'), getValues('archiveUser')) === USER_ROLE_STATUS.ACTIVE) {
      switch (prevStatus) {
        case USER_ROLE_STATUS.INACTIVE:
        case USER_ROLE_STATUS.LOCKED_AND_INACTIVE: {
          setIsLocked(false)
          break
        }
        case USER_ROLE_STATUS.LOCKED: {
          setIsLocked(true)
          break
        }
        default:
      }
    }
  }

  usePhone({
    ids: ['primaryPhoneNumber', 'mobileNumber'],
    setValue,
    isFetched,
    trigger,
  })

  const curentClient = useWatch({
    control,
    name: 'clientName',
  })

  useEffect(() => {
    if (!curentClient) return
    setAccountLoading(true)
    getClientById({ clientId: curentClient }).then((res: any) => {
      setSelectedClientObj(res?.data ?? null)
      getAccountList(curentClient, true).then((res: any) => {
        if (res?.data) {
          setAccountList(
            res?.data?.map((item: any) => ({
              label: item.accountName,
              value: item.accountId,
            })),
          )
          setAccountLoading(false)
        }
      })
    })
  }, [curentClient])

  const isSaveButtonDisabled = isChange
    ? !isChange || !!Object.keys(errors)?.length || !getValues('navigationOptions')?.length
    : !Object.keys(dirtyFields)?.length ||
    !!Object.keys(errors)?.length ||
    !getValues('hasCustomRole') ||
    (getValues('hasCustomRole') && !getValues('navigationOptions')?.length)

  const handleCancel = () => {
    navigate(menuUrls.clientUserSetup)
  }

  const getStatus = (userLocked: boolean, archiveUser: boolean) => {
    if (userLocked && archiveUser) {
      return USER_ROLE_STATUS.LOCKED_AND_INACTIVE
    } else if (userLocked) {
      return USER_ROLE_STATUS.LOCKED
    } else if (archiveUser) {
      return USER_ROLE_STATUS.INACTIVE
    }
    return USER_ROLE_STATUS.ACTIVE
  }

  const onSubmit = async (data: any) => {
    const payload = {
      username: data?.username,
      firstName: data?.firstName,
      lastName: data?.lastName,
      email: data?.emailAddress,
      isNotReceiveOtherMail: data?.noEmailContact,
      primaryPrefix: data?.primaryPhoneNumberRegion,
      primaryPhoneNumber: data?.primaryPhoneNumber,
      mobilePrefix: data?.mobileNumberRegion,
      mobileNumber: data?.mobileNumber,
      clientId: data?.clientName,
      clientNotes: data?.clientNotes,
      accounts: data?.accountNames || [],
      isAssignAllNewAccounts: data?.assignAll,
      status: getStatus(data?.userLocked, data?.archiveUser),
      hasCustomRole: true,
      customRole: getCustomRolePayload(data),
    }

    setLoading(true)
    if (id) {
      putUpdateClientUser(id, payload).then((res: any) => {
        if (!res?.data?.success) {
          switch (res?.data?.code) {
            case SERVER_STATUS_CODES.DUP01_CHECK_STATUS_DUPLICATED:
            case SERVER_STATUS_CODES.DUP05_CHECK_STATUS_DUPLICATED:
              setError('username', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_USERNAME) })
              setLoading(false)
              break
            case SERVER_STATUS_CODES.DUP02_CHECK_STATUS_DUPLICATED:
              setIsConfirmOpen(true)
              setDuplicateUser(res?.data?.oldUser)
              setLoading(false)
              break
            case SERVER_STATUS_CODES.DUP03_CHECK_STATUS_DUPLICATED:
            case SERVER_STATUS_CODES.DUP04_CHECK_STATUS_DUPLICATED:
            case SERVER_STATUS_CODES.DUP06_CHECK_STATUS_DUPLICATED:
              setError('emailAddress', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EMAIL) })
              setLoading(false)
              break
            default:
              break
          }
        } else {
          setLoading(false)
          navigate(menuUrls.clientUserSetup)
          notification.success({
            message: (
              <RichTextField
                richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.CLIENT_USER_UPDATED_SUCCESSFULLY}`, {
                  Username: data.username,
                })}
              />
            ),
          })
        }
      })
    } else {
      setLoading(false)
    }

    setIsConfirmOpen(false)
  }

  const handleNavigate = () => {
    setIsConfirmOpen(false)
    navigate(`${menuUrls.editClientUserSetup}/${duplicateUser?.id}`, { preventScrollReset: true })
  }

  const handleOnchangePhoneInput = (e: any, field: any) => {
    let inputValue = e.target.value
    inputValue = inputValue.trim().replace(/\D/g, '')
    field.onChange(inputValue)
  }

  const handleOnblurPhoneInput = (e: any, field: any) => {
    let inputValue = e.target.value
    inputValue = inputValue.trim()
    field.onChange(inputValue)
  }

  useEffect(() => {
    if (Object.keys(errors)?.length > 0) {
      setIsConfirmOpen(false)
    }
  }, [errors])

  const navigationOptionsWatch = useWatch({ control, name: 'navigationOptions' })
  const permissionsWatch = useWatch({ control, name: 'permissions' })
  const permissions = useSelector(getPermissionsSetup)
  const emails = useSelector(getEmailsSetup)
  const { fields: emailFields, replace } = useFieldArray({ control, name: 'emails' })

  const getCustomRolePayload = (data: any) => {
    return {
      permissionIds: data?.permissions?.filter((x: any) => x?.length > 0) ?? [],
      userNotificationTypeIds: Array.isArray(data?.emails) ? Array.from(data?.emails).map((x: any) => x.emailId) : [],
      navigationOptionIds: data?.navigationOptions ?? [],
      expirationType: data?.expirations ?? EXPIRATION_TYPE.DOES_NOT_EXPIRE,
      expirationMinutes: data?.expirationMinutes ?? null,
      expirationDays: data?.expirationDays ?? null,
    }
  }

  useEffect(() => {
    setDependLoading(true)

    fetchSubModuleItem(true).then((res: any) => {
      if (res?.data) {
        resetField('selectAllNavigations')
        resetField('selectAllEmails')
        dispatch(setUpModules(res?.data))
        setDependLoading(false)
      }
    })

    fetchPermission({ primaryGroupTypeCodeName: priGrpTypCodeName.clientAccess }).then((res: any) => {
      if (res?.data) {
        dispatch(setUpPermissions(res?.data))
        setDefaultPermission(res?.data?.filter((item: any) => item.codeName === DEFAULT_VIEW_ITEMS)?.[0]?.id)
      }
    })

    fetchUserNotificationType('client').then((res: any) => {
      if (res?.data) {
        dispatch(setUpEmails(res?.data))
        setDependLoading(false)
      }
    })
  }, [dispatch, setDependLoading, resetField])

  return (
    <Form name='add-client-user-form' className='form-client-user-container' layout='vertical'>
      {isFetched ? (
        <Fragment>
          {selectedClientObj?.activationDate && (
            <ActivationWarning activationDate={dayjs(selectedClientObj.activationDate)} />
          )}
          <Form.Item label={t('ClientUserSetup:username.title')} className='isRequired'>
            <Controller
              name='username'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  status={errors?.username ? 'error' : ''}
                  placeholder={t('ClientUserSetup:username.placeholder')}
                  onChange={(e) => {
                    field.onChange(e?.target?.value.replace(/\s/g, ''))
                  }}
                  onBlur={(e) => {
                    let e1 = e
                    e1.target.value = e.target.value.trim()
                    field.onChange(e1)
                  }}
                  maxLength={50}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='username' />
            </Typography>
          </Form.Item>
          <Flex style={{ width: '100%' }} gap={24}>
            <Form.Item label={t('ClientUserSetup:firstName.title')} className='isRequired' style={{ width: '100%' }}>
              <Controller
                name='firstName'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    style={{ width: '100%' }}
                    status={errors?.firstName ? 'error' : ''}
                    placeholder={t('ClientUserSetup:firstName.placeholder')}
                    onChange={(e) => {
                      field.onChange(e?.target?.value?.trimStart())
                    }}
                    onBlur={(e) => {
                      let e1 = e
                      e1.target.value = e.target.value.trim()
                      field.onChange(e1)
                    }}
                    maxLength={64}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='firstName' />
              </Typography>
            </Form.Item>

            <Form.Item label={t('ClientUserSetup:lastName.title')} className='isRequired' style={{ width: '100%' }}>
              <Controller
                name='lastName'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    style={{ width: '100%' }}
                    status={errors?.lastName ? 'error' : ''}
                    placeholder={t('ClientUserSetup:lastName.placeholder')}
                    onChange={(e) => {
                      field.onChange(e?.target?.value?.trimStart())
                    }}
                    onBlur={(e) => {
                      let e1 = e
                      e1.target.value = e.target.value.trim()
                      field.onChange(e1)
                    }}
                    maxLength={64}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='lastName' />
              </Typography>
            </Form.Item>
          </Flex>
          <Form.Item label={t('ClientUserSetup:emailAddress.title')} className='isRequired'>
            <Controller
              name='emailAddress'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  status={errors?.emailAddress ? 'error' : ''}
                  placeholder={t('ClientUserSetup:emailAddress.placeholder')}
                  onChange={(e) => {
                    field.onChange(e?.target?.value?.trim())
                  }}
                  maxLength={70}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='emailAddress' />
            </Typography>
          </Form.Item>

          <Flex>
            <Controller
              name='noEmailContact'
              control={control}
              render={({ field }) => (
                <Checkbox {...field} checked={field.value}>
                  {t('ClientUserSetup:noEmailContact')}
                </Checkbox>
              )}
            />
          </Flex>

          <Flex style={{ width: '100%', marginTop: 24 }} gap={24}>
            <Form.Item label={t('ClientUserSetup:primaryPhoneNumber.title')} style={{ width: '100%' }}>
              <Flex gap={8}>
                <Controller
                  name='primaryPhoneNumberRegion'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={items}
                      style={{
                        width: 96,
                        height: 44,
                        borderRadius: 12,
                      }}
                      onBlur={async (event) => {
                        field.onBlur()
                      }}
                    >
                      <Typography.Text>{field?.value}</Typography.Text>
                      <ArrowDownIcon />
                    </Select>
                  )}
                />
                <Controller
                  name='primaryPhoneNumber'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      style={{ width: '100%' }}
                      id='primaryPhoneNumber'
                      status={errors?.primaryPhoneNumber ? 'error' : ''}
                      placeholder={t('ClientUserSetup:primaryPhoneNumber.placeholder')}
                      onChange={(e) => {
                        handleOnchangePhoneInput(e, field)
                      }}
                      onBlur={(e) => {
                        handleOnblurPhoneInput(e, field)
                      }}
                      maxLength={12}
                    />
                  )}
                />
              </Flex>
              <Typography className='error-message' style={{ left: 96 }}>
                <ErrorMessage errors={errors} name='primaryPhoneNumber' />
              </Typography>
            </Form.Item>

            <Form.Item label={t('ClientUserSetup:mobileNumber.title')} style={{ width: '100%' }}>
              <Flex gap={8}>
                <Controller
                  name='mobileNumberRegion'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={items}
                      style={{
                        width: 96,
                        height: 44,
                        borderRadius: 12,
                      }}
                      onBlur={async (event) => {
                        field.onBlur()
                      }}
                    >
                      <Typography.Text>{field?.value}</Typography.Text>
                      <ArrowDownIcon />
                    </Select>
                  )}
                />
                <Controller
                  name='mobileNumber'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      style={{ width: '100%' }}
                      id='mobileNumber'
                      status={errors?.mobileNumber ? 'error' : ''}
                      placeholder={t('ClientUserSetup:mobileNumber.placeholder')}
                      onChange={(e) => {
                        handleOnchangePhoneInput(e, field)
                      }}
                      onBlur={(e) => {
                        handleOnblurPhoneInput(e, field)
                      }}
                      maxLength={12}
                    />
                  )}
                />
              </Flex>
              <Typography className='error-message' style={{ left: 96 }}>
                <ErrorMessage errors={errors} name='mobileNumber' />
              </Typography>
            </Form.Item>
          </Flex>
          <SelectClient
            control={control}
            errors={errors}
            setValue={setValue}
            clientList={clientList}
            resetField={resetField}
            accountList={accountList}
            curentClient={curentClient}
            loading={accountLoading}
            clearErrors={clearErrors}
          />

          {!dependLoading && (
            <>
              <NavigationOptions
                control={control}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                navigationOptionsWatch={navigationOptionsWatch}
                trigger={trigger}
              />
              <Switchers
                listSwitchers={permissions}
                title={t('UserRoles:permissions')}
                name='permissions'
                control={control}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
                isTurnOnFirst
                data={permissionsWatch}
                resetField={resetField}
                trigger={trigger}
                allOptionName='selectAllPermissions'
                defaultPermission={defaultPermission}
              />
              <EmailSwitchers
                listSwitchers={emails}
                title={t('UserRoles:emails')}
                data={emailFields}
                replace={replace}
                control={control}
                setValue={setValue}
                trigger={trigger}
                name='emails'
              />
              <Expirations control={control} errors={errors} trigger={trigger} clearErrors={clearErrors} />
            </>
          )}

          <Flex style={{ width: '100%', marginTop: 24, marginBottom: 24 }} gap={24} justify='space-between'>
            <Col span={12}>
              <Controller
                name='userLocked'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e: CheckboxChangeEvent) => {
                      field.onChange(e)
                      setIsChange(true)
                    }}
                  >
                    {t('ClientUserSetup:userLocked')}
                  </Checkbox>
                )}
              />
            </Col>
            <Col span={12}>
              <Controller
                name='archiveUser'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e)
                      setIsChange(true)
                    }}
                  >
                    {t('ClientUserSetup:archiveUser')}
                  </Checkbox>
                )}
              />
            </Col>
          </Flex>
          <Flex className='button-group' justify='flex-end' gap={24}>
            <Button className='cancel-button' onClick={handleCancel}>
              {t('ClientUserSetup:cancel')}
            </Button>

            <Button
              className='save-button'
              type='primary'
              disabled={isSaveButtonDisabled}
              htmlType='button'
              onClick={
                getStatus(getValues('userLocked'), getValues('archiveUser')) === USER_ROLE_STATUS.ACTIVE &&
                  prevStatus !== USER_ROLE_STATUS.ACTIVE
                  ? () => {
                    checkStatus()
                    setIsConfirmOpen(true)
                  }
                  : handleSubmit((data) => onSubmit(data))
              }
              loading={loading}
              style={loading ? { width: 200 } : {}}
            >
              {t('ClientUserSetup:saveChanges')}
            </Button>
          </Flex>
          <ConfirmModal
            isOpen={isConfirmOpen}
            onClose={() => {
              setIsConfirmOpen(false)
              setLoading(false)
            }}
            onConfirm={
              duplicateUser
                ? handleNavigate
                : handleSubmit((data) => {
                  onSubmit(data)
                })
            }
            Username={duplicateUser?.username}
            isLocked={isLocked}
          />
        </Fragment>
      ) : (
        <Skeleton />
      )}
    </Form>
  )
}

export default EditClientUserSetup
